import { useState, useEffect, useRef } from 'react';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import { submitSketcher } from '../../apiservices/axiosservice/gameservice';
import { useSse, sseManager } from '../../sse';
import { getCurrentCounter } from '../../utils/timeCounter';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { toast } from 'react-toastify';
import SketcherCanvas from './SketcherCanvas';
import SketcherArtGallery from './SketcherArtGallery';
import PlayerSketcherGameOver from './PlayerSketcherGameOver';
import MusicToggle from '../gamelobby/MusicToggle';
import '../gamelobby/HostSketcherPlay.css';

type Phase = 'drawing' | 'gallery' | 'nosubmission' | 'gameover';

const PlayerSketcherPlay: React.FC = () => {
  const gd = getGameDetails();
  const { gameId, playerId } = gd || {};

  // SSE subscriptions
  const currentRound = useSse<SketcherCurrentRound>('sketcher-current-round');
  const roundResult = useSse<SketcherResultsWrapper>('sketcher-round-result');
  const gameStatus = useSse<GameStatusInfo>('game-status');
  const zeroSketchReport = useSse<ZeroSketchReport>('zero-sketch-report');

  // State
  const [phase, setPhase] = useState<Phase>('drawing');
  const [sketchItem, setSketchItem] = useState('');
  const [hint, setHint] = useState('');
  const [roundNumber, setRoundNumber] = useState(1);
  const [totalRounds, setTotalRounds] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [galleryResults, setGalleryResults] = useState<
    SketcherRoundResult[] | null
  >(null);
  const [galleryExpiryTime, setGalleryExpiryTime] = useState<string | null>(
    null
  );
  const [roundReceived, setRoundReceived] = useState(false);

  const submittedRef = useRef(false);
  const startTimeRef = useRef<number>(Date.now());
  const leaderboard = useLeaderboard(gameId);

  // Connect SSE on mount
  useEffect(() => {
    if (gameId) sseManager.connect(gameId);
  }, [gameId]);

  // Handle new round from backend
  useEffect(() => {
    if (!currentRound) return;

    // Reset for new round
    setRoundReceived(true);
    setPhase('drawing');
    setSketchItem(currentRound.sketchItem);
    setHint(currentRound.hint);
    setRoundNumber(currentRound.roundNumber);
    setTotalRounds(currentRound.totalRounds);
    setSubmitted(false);
    submittedRef.current = false;
    setGalleryResults(null);
    setGalleryExpiryTime(null);
    startTimeRef.current = Date.now();

    const seconds = getCurrentCounter(currentRound.currentExpiryTime);
    setTimeLeft(seconds);
    setTotalTime(seconds);
  }, [currentRound]);

  // Timer countdown
  useEffect(() => {
    if (phase !== 'drawing' || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(timer);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
    // Depend on whether the timer is active (timeLeft > 0) rather than the
    // raw value. On the first round the effect first runs while timeLeft is
    // still 0 (initial state) and bails out; when the round SSE later sets a
    // positive time, `timeLeft > 0` flips false->true and the effect re-runs
    // to start the interval. Using the boolean avoids recreating the interval
    // on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeLeft > 0]);

  // Handle round results from backend.
  // Each event is displayed on its own: a new event REPLACES the previous
  // results (no cross-event accumulation).
  useEffect(() => {
    if (!roundResult?.sketcherBatchResults) return;

    setGalleryResults(roundResult.sketcherBatchResults);
    setGalleryExpiryTime(roundResult.currentExpiryTime ?? null);
    setPhase('gallery');
  }, [roundResult]);

  // Handle game over
  useEffect(() => {
    if (gameStatus?.status === 'FINISHED') {
      setPhase('gameover');
    }
  }, [gameStatus]);

  // Handle zero sketch report — no submissions received for the round
  useEffect(() => {
    if (!zeroSketchReport) return;
    setRoundNumber(zeroSketchReport.currentRound);
    setTotalRounds(zeroSketchReport.totalRounds);
    setPhase('nosubmission');
  }, [zeroSketchReport]);

  const handleSubmit = (imageBase64: string, isTimeout: boolean) => {
    doSubmit(imageBase64, isTimeout);
    // Move to gallery waiting state
    setTimeout(() => setPhase('gallery'), 1200);
  };

  const doSubmit = (imageBase64: string, isTimeout: boolean) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true);

    // On timeout the player used the whole round, so report the full allotted
    // time. On a manual submit, measure elapsed time but clamp to totalTime so
    // timer drift can never report more than the round length.
    const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
    const timeTaken =
      isTimeout && totalTime > 0
        ? totalTime
        : totalTime > 0
          ? Math.min(elapsed, totalTime)
          : elapsed;

    submitSketcher({
      gameId: gameId!,
      playerId: playerId!,
      imageUrl: imageBase64,
      timeTaken,
    })
      .then(() => {
        // Submission successful, wait for results
      })
      .catch((err) => {
        toast.error('Failed to submit sketch. Please try again.');
        // Allow resubmission on error
        submittedRef.current = false;
        setSubmitted(false);
      });
  };

  // Render based on phase
  if (phase === 'gameover') {
    return (
      <PlayerSketcherGameOver
        leaderboard={leaderboard}
        yourPlayerId={playerId}
      />
    );
  }

  if (phase === 'nosubmission') {
    return (
      <div className="sketcher-waiting">
        <div className="sketcher-waiting__content">
          <div className="sketcher-waiting__animation">
            <span className="sketcher-waiting__emoji">🚫</span>
            <div className="sketcher-waiting__dots">
              <span className="sketcher-waiting__dot" />
              <span className="sketcher-waiting__dot" />
              <span className="sketcher-waiting__dot" />
            </div>
          </div>
          <h2 className="sketcher-waiting__title">
            No Sketch Submission Received
          </h2>
          <p className="sketcher-waiting__subtitle">
            Time ran out and no sketches were submitted for this round.
          </p>
          <span className="sketcher-waiting__round">
            Round {roundNumber}/{totalRounds}
          </span>
        </div>
      </div>
    );
  }

  if (phase === 'gallery') {
    return (
      <>
        <MusicToggle />
        <SketcherArtGallery
          results={galleryResults}
          roundNumber={roundNumber}
          totalRounds={totalRounds}
          expiryTime={galleryExpiryTime}
        />
      </>
    );
  }

  // Drawing phase
  if (!roundReceived) {
    return (
      <div className="skc sketcher-waiting">
        <div className="sketcher-waiting__content">
          <div className="sketcher-waiting__animation">
            <span className="sketcher-waiting__emoji">🎨</span>
            <div className="sketcher-waiting__dots">
              <span className="sketcher-waiting__dot" />
              <span className="sketcher-waiting__dot" />
              <span className="sketcher-waiting__dot" />
            </div>
          </div>
          <h2 className="sketcher-waiting__title">Game Not Yet Received</h2>
          <p className="sketcher-waiting__subtitle">
            The round data hasn't arrived yet. Please wait for the host to
            start.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MusicToggle />
      <SketcherCanvas
        sketchItem={sketchItem}
        hint={hint}
        roundNumber={roundNumber}
        totalRounds={totalRounds}
        timeLeft={timeLeft}
        totalTime={totalTime}
        onSubmit={handleSubmit}
        submitted={submitted}
      />
    </>
  );
};

export default PlayerSketcherPlay;
